import type { SerializedTodoItem } from '@pique/core/modules/properties/services/schema';

function normalizeActionItemText(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function countByTitle(
  items: readonly SerializedTodoItem[]
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = normalizeActionItemText(item.title);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function findMatchingDetachedItem(
  rowItem: SerializedTodoItem,
  detachedItems: readonly SerializedTodoItem[],
  consumedIds: ReadonlySet<string>,
  rowTitleCounts: ReadonlyMap<string, number>
): SerializedTodoItem | undefined {
  const rowTitle = normalizeActionItemText(rowItem.title);
  if (!rowTitle) return undefined;

  const availableMatches = detachedItems.filter(
    item =>
      !consumedIds.has(item.id) &&
      normalizeActionItemText(item.title) === rowTitle
  );
  if (availableMatches.length === 0) return undefined;

  const rowContext = normalizeActionItemText(rowItem.context);
  const contextMatches = availableMatches.filter(
    item => normalizeActionItemText(item.context) === rowContext
  );
  if (contextMatches.length === 1) {
    return contextMatches[0];
  }

  if (availableMatches.length === 1 && rowTitleCounts.get(rowTitle) === 1) {
    return availableMatches[0];
  }

  return undefined;
}

function mergeMatchedRowItem(
  rowItem: SerializedTodoItem,
  matchedItem: SerializedTodoItem
): SerializedTodoItem {
  const merged: SerializedTodoItem = {
    ...matchedItem,
    ...rowItem,
    id: rowItem.id,
  };

  if (matchedItem.id === rowItem.id) {
    return merged;
  }

  if (!rowItem.context?.trim() && matchedItem.context?.trim()) {
    merged.context = matchedItem.context;
  }

  if (
    (!Array.isArray(rowItem.breakdownSteps) ||
      rowItem.breakdownSteps.length === 0) &&
    Array.isArray(matchedItem.breakdownSteps) &&
    matchedItem.breakdownSteps.length > 0
  ) {
    merged.breakdownSteps = matchedItem.breakdownSteps;
  }

  if (
    (!Array.isArray(rowItem.assignedTo) || rowItem.assignedTo.length === 0) &&
    Array.isArray(matchedItem.assignedTo) &&
    matchedItem.assignedTo.length > 0
  ) {
    merged.assignedTo = matchedItem.assignedTo;
  }

  return merged;
}

export function mergeDatabaseRowActionItems({
  rows,
  rowItems,
  existingActionItems,
  databaseId,
}: {
  rows: readonly string[];
  rowItems: readonly SerializedTodoItem[];
  existingActionItems: readonly SerializedTodoItem[];
  databaseId?: string;
}): SerializedTodoItem[] {
  const rowIdSet = new Set(rows);
  const consumedExistingIds = new Set<string>(rows);
  const existingById = new Map(
    existingActionItems.map(item => [item.id, item])
  );
  const detachedItems = existingActionItems.filter(
    item => item.title?.trim() && !rowIdSet.has(item.id)
  );
  const rowTitleCounts = countByTitle(rowItems);

  const mergedRowItems = rowItems.map(rowItem => {
    const directMatch = existingById.get(rowItem.id);
    const detachedMatch =
      directMatch ??
      findMatchingDetachedItem(
        rowItem,
        detachedItems,
        consumedExistingIds,
        rowTitleCounts
      );

    if (!detachedMatch) return rowItem;

    consumedExistingIds.add(detachedMatch.id);
    return mergeMatchedRowItem(rowItem, detachedMatch);
  });

  const nonDbItems = detachedItems.filter(item => {
    if (consumedExistingIds.has(item.id)) return false;
    return !databaseId || item.databaseId !== databaseId;
  });

  return [...mergedRowItems, ...nonDbItems];
}
